import type { ReactNode } from "react";

export interface RankEntry {
  rank: number;
  name: string;
  score?: number;
  type1?: string;
  type2?: string;
  is_shadow?: boolean;
  best_fast_move?: string;
  best_charge_move_1?: string;
  best_charge_move_2?: string;
}

interface Props {
  entries: RankEntry[];
  limit?: number;
  empty?: ReactNode;
}

const MEDALS = ["🥇", "🥈", "🥉"];

const TYPE_BADGE: Record<string, string> = {
  fire:     "bg-orange-500/15 text-orange-400 border-orange-500/20",
  water:    "bg-blue-500/15 text-blue-400 border-blue-500/20",
  grass:    "bg-green-500/15 text-green-400 border-green-500/20",
  electric: "bg-yellow-400/15 text-yellow-300 border-yellow-400/20",
  psychic:  "bg-pink-500/15 text-pink-400 border-pink-500/20",
  ice:      "bg-cyan-400/15 text-cyan-300 border-cyan-400/20",
  dragon:   "bg-indigo-500/15 text-indigo-400 border-indigo-500/20",
  dark:     "bg-gray-600/15 text-gray-400 border-gray-500/20",
  fairy:    "bg-pink-400/15 text-pink-300 border-pink-400/20",
  normal:   "bg-gray-400/10 text-gray-300 border-gray-400/15",
  fighting: "bg-red-600/15 text-red-400 border-red-600/20",
  flying:   "bg-sky-500/15 text-sky-400 border-sky-500/20",
  poison:   "bg-purple-500/15 text-purple-400 border-purple-500/20",
  ground:   "bg-amber-600/15 text-amber-500 border-amber-600/20",
  rock:     "bg-amber-500/15 text-amber-400 border-amber-500/20",
  bug:      "bg-lime-500/15 text-lime-400 border-lime-500/20",
  ghost:    "bg-violet-500/15 text-violet-400 border-violet-500/20",
  steel:    "bg-slate-400/15 text-slate-300 border-slate-400/20",
};

function TypeBadge({ type }: { type: string }) {
  const cls = TYPE_BADGE[type.toLowerCase()] ?? "bg-gray-400/10 text-gray-300 border-gray-400/15";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border leading-none capitalize ${cls}`}
    >
      {type}
    </span>
  );
}

function MoveLine({ fast, charge1, charge2 }: { fast?: string; charge1?: string; charge2?: string }) {
  if (!fast && !charge1) return null;
  const charges = [charge1, charge2].filter(Boolean).join(" / ");
  return (
    <p className="text-[11px] text-white/35 mt-0.5 truncate">
      {fast}
      {fast && charges && <span className="mx-1 text-white/20">·</span>}
      {charges}
    </p>
  );
}

export default function RankList({ entries, limit = 10, empty }: Props) {
  const visible = entries.slice(0, limit);
  if (visible.length === 0) {
    return <>{empty}</>;
  }

  return (
    <ol className="space-y-1">
      {visible.map((entry, i) => (
        <li
          key={`${entry.name}-${i}`}
          className={`flex items-start gap-3 px-3 py-2.5 rounded-xl transition-colors ${
            i < 3
              ? "bg-white/[0.04] border border-white/[0.06]"
              : "hover:bg-white/[0.03]"
          }`}
        >
          {/* Rank badge */}
          <span className="w-6 text-center flex-shrink-0 mt-0.5">
            {i < 3
              ? MEDALS[i]
              : <span className="text-xs text-white/30 tabular-nums">{i + 1}</span>}
          </span>

          {/* Name + types + moveset */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-medium text-white/90">{entry.name}</span>
              {entry.is_shadow && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border leading-none bg-violet-500/20 text-violet-300 border-violet-500/30">
                  Shadow
                </span>
              )}
              {entry.type1 && <TypeBadge type={entry.type1} />}
              {entry.type2 && <TypeBadge type={entry.type2} />}
            </div>
            <MoveLine
              fast={entry.best_fast_move}
              charge1={entry.best_charge_move_1}
              charge2={entry.best_charge_move_2}
            />
          </div>

          {/* Score */}
          {entry.score !== undefined && (
            <span className="text-xs text-white/30 tabular-nums flex-shrink-0 mt-1">
              {entry.score.toFixed(1)}
            </span>
          )}
        </li>
      ))}
    </ol>
  );
}
